/**
 * Disposal > Companies detail — "Contacts": the people on the company's tenant, read-only.
 *
 * GET /v1/platform/disposal-companies/:id/users (PLATFORM_ADMIN, MANAGE_DISPOSAL_PORTAL_ACCESS).
 * Shown under the primary contact in CompanyPanel. Its own file and its own query hook, so the
 * Suspend / Reactivate work in DisposalCompaniesTab.tsx and hooks.ts stays separate.
 *
 * There is no last sign-in: nothing records a web sign-in today. "Signed up" says whether the
 * person has a sign-in identity yet.
 */

import * as React from 'react';
import { useApiQuery, useApiOpts } from '@lodex-pro/web-shared';
import { Pill, subText } from '../../ui/bits';
import { DISPOSAL_COMPANIES_KEY } from './hooks';

export interface DisposalCompanyUser {
  readonly userId: string;
  readonly fullName: string | null;
  readonly email: string | null;
  readonly role: string;
  readonly status: string;
  readonly invited: boolean;
  readonly joinedAt: string | null;
  readonly activatedAt: string | null;
}

export function useDisposalCompanyUsers(
  companyId: string,
): ReturnType<typeof useApiQuery<{ users: DisposalCompanyUser[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ users: DisposalCompanyUser[] }>(
    opts,
    `/v1/platform/disposal-companies/${companyId}/users`,
    { queryKey: [...DISPOSAL_COMPANIES_KEY, companyId, 'users'], staleTime: 30_000 },
  );
}

const ROLE_LABELS: Readonly<Record<string, string>> = {
  DISPOSAL_ADMIN: 'Disposal admin',
};

const STATUS_LABELS: Readonly<
  Record<string, { label: string; tone: 'positive' | 'warning' | 'muted' }>
> = {
  ACTIVE: { label: 'Active', tone: 'positive' },
  SUSPENDED: { label: 'Suspended', tone: 'warning' },
  OFFBOARDED: { label: 'Removed', tone: 'muted' },
};

/** Local-by-context date (DEC-004 — never render UTC). */
function fmtDate(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function DisposalCompanyContacts({ companyId }: { companyId: string }): React.JSX.Element {
  const q = useDisposalCompanyUsers(companyId);
  // Defensive: anything but a users array (loading, error, an unexpected shape) shows no rows.
  const users = Array.isArray(q.data?.users) ? q.data.users : [];

  return (
    <div data-testid="disposal-company-contacts" style={{ marginTop: '12px' }}>
      <h4 style={{ fontSize: '13px', fontWeight: 600, margin: 0 }}>Contacts</h4>
      {q.isLoading ? (
        <p style={{ ...subText, marginTop: '4px' }}>Loading…</p>
      ) : q.isError ? (
        <p style={{ ...subText, marginTop: '4px' }}>Couldn’t load this company’s users.</p>
      ) : users.length === 0 ? (
        <p style={{ ...subText, marginTop: '4px' }}>No users on this company yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0' }}>
          {users.map((u) => {
            const status = STATUS_LABELS[u.status] ?? { label: u.status, tone: 'muted' as const };
            return (
              <li
                key={u.userId}
                data-testid="disposal-company-contact"
                style={{ display: 'flex', gap: '8px', alignItems: 'baseline', padding: '4px 0' }}
              >
                <span style={{ fontSize: '13px', fontWeight: 500 }}>{u.fullName ?? '—'}</span>
                <span style={{ ...subText }}>{u.email ?? 'no email'}</span>
                <span style={{ ...subText }}>{ROLE_LABELS[u.role] ?? u.role}</span>
                <Pill label={status.label} tone={status.tone} />
                <span style={{ ...subText }}>
                  {u.invited ? `Signed up · joined ${fmtDate(u.joinedAt)}` : 'Not signed up yet'}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
